import { SidebarHeader } from "@/components/sidebar/components/SidebarHeader.tsx";
import { SidebarConfigItem } from "@/components/sidebar/sidebar-service.tsx";
import { SidebarPanelItem, Title, Box, Stack } from "@skailer-com/uikit";
import { getReportsVisibleToEmployee } from "@/pages/reporting/reporting.types.ts";
import {
  isWidgetChart,
  PEOPLE_ANALYTICS_WIDGET_PARAM,
} from "@/pages/reporting/employee/people-analytics/people-analytics.types.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { usePeopleAnalyticsWidgets } from "@/queries/reporting/use-people-analytics.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ApiPeopleAnalyticsWidget } from "@/types/reporting/people-analytics.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const PEOPLE_ANALYTICS_BASE = "/reporting/employee/people-analytics";

// Ссылка несёт только id: отчёт сам разворачивает виджет в параметры билдера
function buildWidgetTo(widget: ApiPeopleAnalyticsWidget): string {
  const isChart = isWidgetChart(widget);
  const params = new URLSearchParams();
  params.set(PEOPLE_ANALYTICS_WIDGET_PARAM, String(widget.id));
  return `${PEOPLE_ANALYTICS_BASE}${isChart ? "/chart" : ""}?${params.toString()}`;
}

export const SidebarReporting = () => {
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();

  const { data: employee } = useEmployeeMe();
  const { pathname } = useLocation();

  const widgets = usePeopleAnalyticsWidgets(canIEdit(ApiPermission.reporting_people_analytics));
  const ownWidgets = (widgets.data ?? []).filter((widget) => widget.is_own);

  const menuConfig = useMemo<SidebarConfigItem[]>(
    () => [
      {
        title: t("common:overview"),
        to: "/reporting",
        isActive: pathname.endsWith("/reporting"),
      },
    ],
    [pathname, t]
  );

  const menu = useMemo(() => {
    if (!employee) return [];

    return getReportsVisibleToEmployee(employee);
  }, [employee]);

  return (
    <>
      <SidebarHeader title={t("common:reports")} />
      <Stack className={"px-4 pb-6"}>
        <Stack gap={"sm"}>
          {menuConfig.map((item, index) => (
            <SidebarPanelItem
              to={item.to || "/"}
              key={"sidebar-" + index}
              isActive={item.isActive}
              title={item.title}
            />
          ))}
        </Stack>
        {menu.map((item, index) => (
          <Fragment key={`sidebar-menu-group-${index}`}>
            <Box className={"px-2"}>
              <Title header={item.title} size={"xs"} paddingBottom paddingTop />
            </Box>
            <Stack gap={"xs"}>
              {item.items.map((settingsItem) => (
                <SidebarPanelItem
                  key={`'secondary-menu-${settingsItem.title}`}
                  to={settingsItem.to}
                  isActive={pathname.startsWith(settingsItem.to)}
                  title={settingsItem.menuTitle}
                />
              ))}
            </Stack>
          </Fragment>
        ))}
        {ownWidgets.length > 0 && (
          <>
            <Box className={"px-2"}>
              <Title header={t("reporting:my_reports")} size={"xs"} paddingBottom paddingTop />
            </Box>
            <Stack gap={"xs"}>
              {ownWidgets.map((widget) => (
                <SidebarPanelItem
                  key={`widget-${widget.id}`}
                  to={buildWidgetTo(widget)}
                  isActive={false}
                  title={widget.title}
                />
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </>
  );
};
